import React, { useState, useEffect, useContext, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import Tippy from '@tippyjs/react';
import 'tippy.js/dist/tippy.css';
import { useFormSubmission, submitForm } from '../../CheckFormHooks';
import { openAlert } from '../../Util/Util';
import { FormContext } from '../../Contexts/FormContext';
import { useFormAttention } from '../../Hooks/useFormAttention';

// Make window.setButtonIndicator available globally for the jQuery code
window.setButtonIndicator = (state) => {
	// This function will be overridden by the React component
	console.log(`Global indicator setter called with state: ${state}`);
};

const NonSEButtons = (props) => {
	const { t } = useTranslation();
	const { options } = useContext(FormContext);

	const {formFields} = props;
	const {page, form_components} = formFields;

	const pageData = useSelector(state => state.pageData);
	const {hook_status, preview, funnel_page_id, controller, acct_id, iframe} = pageData;
	const fv = useSelector(state => state.fv);

	// Button ref
	const buttonRef = useRef(null);
	const turnstileSubmitRetryRef = useRef(false);
	
	// Processing state tracking
	const [isProcessing, setIsProcessing] = useState(false);
	const [indicator, setIndicator] = useState('off');
	
	// Get form data function from useFormSubmission
	const { getFormData } = useFormSubmission();
	
	// Initialize form attention hook
	const { validateBeforeNext } = useFormAttention('form-inputs');
	
	// Helper function to reset button state
	const resetButtonState = () => {
		console.log('Reset button state called');
		setIsProcessing(false);
		setIndicator('off');
	};

	const clearTurnstileState = () => {
		const form = document.getElementById('reg-form');
		const tokenFieldNames = [
			'cf-turnstile-response',
			'turnstile-response',
			'cf-turnstile-token',
			'g-recaptcha-response',
			'token',
			'cf_turnstile_response',
			'turnstile_response',
			'cf_challenge_response'
		];
		if (form) {
			delete form.dataset.turnstileToken;
			delete form.dataset.turnstileTimestamp;
			tokenFieldNames.forEach((fieldName) => {
				const tokenInput = form.querySelector(`input[name="${fieldName}"]`);
				if (tokenInput) {
					tokenInput.value = '';
				}
			});
			const widgetId = form.dataset.turnstileWidgetId;
			if (widgetId && window.turnstile && typeof window.turnstile.reset === 'function') {
				try {
					window.turnstile.reset(widgetId);
				} catch (e) {
				}
			}
		}
		window.turnstileToken = '';
	};

	const isTurnstileErrorMessage = (message) => {
		if (!message || typeof message !== 'string') {
			return false;
		}
		const normalized = message.toLowerCase();
		return normalized.includes('security verification') ||
			normalized.includes('turnstile') ||
			normalized.includes('驗證') ||
			normalized.includes('expired');
	};
	
	// Function to handle the form submission
	const handleSubmit = async (event) => {
		// Prevent default behavior
		if (event) {
			event.preventDefault();
			event.stopPropagation();
		}
		
		// Validate form attention before proceeding
		if (!validateBeforeNext(event)) {
			return; // Validation failed, stop here
		}
		
		console.log('Submit handler called, isProcessing:', isProcessing);
		
		// Get the button reference
		const submitBtn = buttonRef.current;
		if (!submitBtn) {
			console.error('Button reference not found');
			return;
		}
		
		// Check if button should be active
		if (isProcessing || hook_status !== 'live' || preview) {
			console.log('Button is processing or disabled, ignoring click');
			return;
		}
		
		console.log('Proceeding with submission');
		
		// Update processing state
		setIsProcessing(true);
		setIndicator('on');
		
		// Get the form
		const form = document.getElementById('reg-form');
		if (!form) {
			console.error('Form not found');
			console.log('Resetting button state (form not found)');
			resetButtonState();
			return;
		}
		
		try {
			// Validate form
			console.log('Starting FormValidation check...');
			if (!fv || typeof fv.validate !== 'function') {
				console.error('FormValidation not ready (fv is null)');
				resetButtonState();
				openAlert(t('表單驗證尚未就緒，請重新整理後再試。'), t);
				return;
			}
			const status = await fv.validate();
			console.log('FormValidation status:', status);
			if (status !== 'Valid') {
				console.log('Form validation failed, resetting button');
				resetButtonState();
				return;
			}
			console.log('FormValidation passed, proceeding with submission');
			
			// Hardcode the AJAX URL format
			let ajaxUrl = '';
			switch (controller) {
				case 'Member':
					ajaxUrl = `/${controller}/ajax-check-form/${acct_id}`;
					break;
				default:
					ajaxUrl = `/${controller}/ajax-check-form/${funnel_page_id}`;
					break;
			}
			console.log(`Submitting directly to URL: ${ajaxUrl}`);
			
			// Build options for form submission (ensure controller is set)
			const submitOptions = {
				controller,
				funnel_page_id,
				t: t,
				openAlert: (message) => openAlert(message, t),
				ajaxUrl,
				turnstileRequired: Boolean(pageData.turnstile_required || window.__form_turnstile_required__)
			};
			// Use generic submitForm to route to the correct controller handler
			const result = await submitForm(submitBtn, form, submitOptions);
			console.log('Form submission result:', result);
			
			// If submission was not successful, reset the button state and display the message
			if (!result.success) {
				if (result.require_turnstile && typeof window.__set_turnstile_required__ === 'function') {
					window.__set_turnstile_required__();
				}
				if (isTurnstileErrorMessage(result.message) && !turnstileSubmitRetryRef.current && typeof window.__ensure_turnstile_token__ === 'function') {
					turnstileSubmitRetryRef.current = true;
					clearTurnstileState();
					setIsProcessing(true);
					setIndicator('on');
					const retryResult = await submitForm(submitBtn, form, submitOptions);
					if (retryResult.success) {
						turnstileSubmitRetryRef.current = false;
						return;
					}
					if (!isTurnstileErrorMessage(retryResult.message)) {
						turnstileSubmitRetryRef.current = false;
						resetButtonState();
						if (retryResult.message) {
							openAlert(retryResult.message);
						}
						return;
					}
				}
				resetButtonState();
				if (isTurnstileErrorMessage(result.message)) {
					clearTurnstileState();
				}
				if (result.message) {
					openAlert(result.message);
				}
			} else {
				turnstileSubmitRetryRef.current = false;
			}
		} catch (error) {
			console.error('Form submission error:', error);
			resetButtonState();
			
			// Display the specific error message if available
			// First check for a message in the error object or its response
			let errorMessage = null;
			
			if (error.message) {
				errorMessage = error.message;
			} else if (error.response && error.response.data) {
				// For axios errors, try to get the message from the response
				const responseData = error.response.data;
				errorMessage = responseData.rm || responseData.message || responseData.error;
			}
			
			// If no specific error message was found, use a generic one
			if (!errorMessage) {
				errorMessage = '提交表單時發生錯誤，請重試。';
			}
			if (isTurnstileErrorMessage(errorMessage)) {
				clearTurnstileState();
			}
			
			openAlert(errorMessage, t);
		}
	};
	
	// Set up global functions
	useEffect(() => {
		// Create global indicator setter
		window.setButtonIndicator = (state) => {
			console.log('Global setButtonIndicator called with:', state);
			setIndicator(state);
			setIsProcessing(state === 'on');
			
			// Debug output current state
			console.log('Processing state set to:', state === 'on');
			
			// Ensure button state is correctly synced with DOM
			const submitBtn = buttonRef.current;
			if (submitBtn) {
				submitBtn.setAttribute('data-kt-indicator', state);
				submitBtn.disabled = (state === 'on' || hook_status !== 'live' || preview);
			}
		};
		
		return () => {
			window.setButtonIndicator = () => {};
		};
	}, [hook_status, preview, t]);

	var tippyContent = '';
	switch (hook_status) {
		case 'warmup':
			tippyContent = t('活動即將開始');
			break;

		case 'expired':
			tippyContent = t('活動已結束');
			break;
	}

	// Use React's onClick directly, and disable the button when processing
	return (
		<div className="button_area">
			<Tippy
				theme="primary"
				content={tippyContent}
				disabled={tippyContent === ''}
			>
				<span>
					<button
						type="button"
						id="submit-btn"
						ref={buttonRef}
						className={`btn btn-danger btn-md col-8 btn-lg form-btn ${hook_status !== 'live' ? 'disabled' : ''}`}
						disabled={isProcessing || hook_status !== 'live' || preview}
						data-kt-indicator={indicator}
						onClick={handleSubmit}
					>
						<span className="indicator-label">{options?.submit_text || t('提交')}</span>
						<span className="indicator-progress">
							{t('請稍候...')}<span className="spinner-border spinner-border-sm align-middle ms-2"></span>
						</span>
					</button>
				</span>
			</Tippy>
		</div>
	);
};

export default NonSEButtons;